import React, { useCallback, useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { AgTableCard, Button, Badge } from '../../UI';
import { useEPMContext } from '../../../contexts/EPMContext';
import { useUserContext } from '../../../contexts/UserContext';
import { permission } from '../../../config/permission';
import LocationFieldModal from './LocationFieldModal';
import ConfirmModal from '../../UI/Modal/confirmModal';

const Locations = () => {
    const { testLocations = [], loading: contextLoading, fetchTestLocations, deleteTestLocation, reorderTestLocations } = useEPMContext();
    const { checkPermission } = useUserContext();
    const canCreate = checkPermission(permission.XCM_SETTINGS_LOCATIONS_CREATE);
    const canUpdate = checkPermission(permission.XCM_SETTINGS_LOCATIONS_UPDATE);
    const canDelete = checkPermission(permission.XCM_SETTINGS_LOCATIONS_DELETE);

    const [actionLoading, setActionLoading] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const rowData = useMemo(() => [...testLocations].sort((a, b) => Number(a.order) - Number(b.order)), [testLocations]);

    const openCreateModal = () => { setSelectedLocation(null); setShowModal(true); };
    const openEditModal = useCallback((location) => { setSelectedLocation(location); setShowModal(true); }, []);

    const confirmDelete = async () => {
        if (!deleteTarget?._id) return;
        setDeleteLoading(true);
        try {
            await deleteTestLocation(deleteTarget._id);
            toast.success('Location field deleted successfully.');
            setDeleteTarget(null);
        } catch (error) {
            toast.error(error?.message || 'Unable to delete the location field.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleRowDragEnd = useCallback(async (event) => {
        const orderedIds = [];
        event.api.forEachNode((node) => { if (node.data?._id) orderedIds.push(String(node.data._id)); });
        if (!orderedIds.length) return;
        setActionLoading(true);
        try {
            await reorderTestLocations({ order_ids: orderedIds });
            toast.success('Location field order updated.');
        } catch (error) {
            toast.error(error?.message || 'Unable to reorder location fields.');
            await fetchTestLocations();
        } finally { setActionLoading(false); }
    }, [fetchTestLocations, reorderTestLocations]);

    const columnDefs = useMemo(() => [
        { headerName: 'Order', field: 'order', rowDrag: canUpdate, width: 105, sortable: false, filter: false },
        { headerName: 'Label', field: 'label', flex: 1.4, minWidth: 160 },
        { headerName: 'Key', field: 'key', flex: 1.2, minWidth: 150 },
        { headerName: 'Field Type', field: 'field_type', minWidth: 135, valueFormatter: ({ value }) => value === 'text_box' ? 'Text box' : 'Dropdown' },
        { headerName: 'Options', field: 'field_options', flex: 1.5, minWidth: 170, valueFormatter: ({ value }) => Array.isArray(value) && value.length ? value.join(', ') : '—' },
        { headerName: 'Conflict Role', field: 'conflict_role', minWidth: 140 },
        { headerName: 'Required', field: 'required', width: 115, valueFormatter: ({ value }) => value ? 'Yes' : 'No' },
        ...(canUpdate || canDelete ? [{
            headerName: 'Actions', field: 'actions', width: 130, sortable: false, filter: false,
            cellRenderer: ({ data }) => (
                <div className="stride-table-actions">
                    {canUpdate && (
                        <button className="stride-table-btn stride-table-btn-edit" type="button" title="Edit field" aria-label={`Edit ${data.label}`} onClick={() => openEditModal(data)}>
                            <Pencil size={15} />
                        </button>
                    )}
                    {canDelete && (
                        <button className="stride-table-btn stride-table-btn-delete" type="button" title="Delete field" aria-label={`Delete ${data.label}`} onClick={() => setDeleteTarget(data)}>
                            <Trash2 size={15} />
                        </button>
                    )}
                </div>
            )
        }] : []),
    ], [canUpdate, canDelete, openEditModal]);

    return (
        <div className="epm-locations">
            <div className="epm-settings__section-heading">
                <div>
                    <h2 className="text-lg font-bold text-stride-heading">Locations</h2>
                    <p className="text-xs text-stride-muted mt-1">Drag rows to control the order shown in the test workflow.</p>
                </div>
                <div className="flex items-center gap-2.5">
                    <Badge variant="primary" appearance="soft">{rowData.length} {rowData.length === 1 ? 'field' : 'fields'}</Badge>
                    {canCreate && (
                        <Button
                            variant="primary"
                            size="sm"
                            icon={<Plus size={15} />}
                            onClick={openCreateModal}
                        >
                            Add New Field
                        </Button>
                    )}
                </div>
            </div>
            <AgTableCard
                tableId="epm-settings-locations"
                theme="ag-theme-quartz"
                loading={contextLoading || actionLoading}
                rowData={rowData}
                columnDefs={columnDefs}
                getRowId={({ data }) => data._id}
                rowDragManaged={true}
                animateRows={true}
                onRowDragEnd={handleRowDragEnd}
                pagination={false}
                emptyTitle="No location fields found"
                emptyMessage="Drag rows to control the order shown in the test workflow."
            />
            {showModal && <LocationFieldModal location={selectedLocation} totalFields={rowData.length} onClose={() => setShowModal(false)} onSaved={() => setShowModal(false)} />}
            {deleteTarget && (
                <ConfirmModal
                    title="Delete Location Field"
                    description={`Are you sure you want to delete the “${deleteTarget.label}” location field?`}
                    ConfirmText="Delete"
                    loading={deleteLoading}
                    hideModal={() => setDeleteTarget(null)}
                    handleConfirmation={confirmDelete}
                />
            )}
        </div>
    );
};

export default Locations;
